import { accentPassesAA, BRAND_SURFACES, BRAND_TEXT, paletteFromAccent, TEAMNA_ACCENT_ROLES, type MyBusiness } from "@teamna/shared";
import { useState } from "react";
import { Button, Field, FormSection, InlineError, SegmentedControl, SwatchPicker, showToast } from "../../components/index.ts";
import { useSetAppAccent } from "@teamna/web-client/hooks/use-business-logos.ts";
import { SaveBar } from "../setup/frame.tsx";

/**
 * Settings, Brand: the colour of the Teamna app (mobile V2, slice 2.2; strategy repo
 * `teamna/strategy/mobile-v2-and-white-label.md`, decision D3).
 *
 * Buttons and links on the phone use this colour while the business is open, for the team and the
 * members. Desk stays Teamna blue. The owner picks Teamna blue, the colour of the logo, or another
 * colour. Any colour is safe: the app makes a light colour darker until white text on it passes
 * WCAG AA, and the preview shows the colour people really see, in light and dark mode.
 */

type Choice = "teamna" | "logo" | "custom";

const HEX = /^#[0-9a-f]{6}$/;

/** Tested starting points. Each one is a colour of its own, not a shade of Teamna blue. */
const SUGGESTED = [
  { value: "#0f766e", label: "Teal" },
  { value: "#15803d", label: "Green" },
  { value: "#b91c1c", label: "Red" },
  { value: "#c2410c", label: "Orange" },
  { value: "#6d28d9", label: "Purple" },
  { value: "#be185d", label: "Pink" },
  { value: "#1e3a8a", label: "Navy" },
  { value: "#111827", label: "Black" },
] as const;

function choiceFor(current: string | null, logoAccent: string | null): Choice {
  if (current === null) return "teamna";
  return logoAccent !== null && current === logoAccent ? "logo" : "custom";
}

/** `#ABC123`, `abc123` and ` #abc123 ` all become `#abc123`. Null when it is not a colour. */
export function normaliseHex(input: string): string | null {
  const trimmed = input.trim().toLowerCase();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return HEX.test(withHash) ? withHash : null;
}

export function AppColour({ business, logoAccent }: { business: MyBusiness; logoAccent: string | null }) {
  const current = business.appAccent ?? null;
  const logo = logoAccent?.toLowerCase() ?? null;
  const [choice, setChoice] = useState<Choice>(() => choiceFor(current, logo));
  const [customText, setCustomText] = useState(() => (current && current !== logo ? current : SUGGESTED[0].value));
  const save = useSetAppAccent(business.businessId);

  const custom = normaliseHex(customText);
  const picked = choice === "teamna" ? null : choice === "logo" ? logo : custom;
  const invalid = choice === "custom" && custom === null;
  const unchanged = !invalid && picked === current;

  const options: { value: Choice; label: string }[] = [
    { value: "teamna", label: "Teamna blue" },
    ...(logo ? [{ value: "logo" as const, label: "Logo colour" }] : []),
    { value: "custom", label: "Another colour" },
  ];

  function submit() {
    if (invalid || unchanged) return;
    save.mutate(
      { accent: picked },
      { onSuccess: () => showToast("The app colour is saved. People see it on their phones in a few minutes.") },
    );
  }

  return (
    <FormSection
      title="Colour in the Teamna app"
      description="Buttons and links in the Teamna app use this colour when people open your business. This is for your team and your members. Desk stays Teamna blue."
    >
      <div className="flex flex-col gap-4 py-4">
        <SegmentedControl label="Colour in the Teamna app" options={options} value={choice} onChange={setChoice} />

        {choice === "custom" ? (
          <div className="flex flex-col gap-3">
            <SwatchPicker
              label="Suggested colours"
              options={SUGGESTED.map((row) => ({ value: row.value as string, label: row.label, color: row.value }))}
              value={custom ?? ""}
              onChange={(value) => setCustomText(value)}
            />
            <Field
              label="Colour code"
              hint="Six characters after #, for example #0f766e."
              value={customText}
              maxLength={7}
              spellCheck={false}
              autoCapitalize="off"
              error={invalid ? "Write a colour code like #0f766e." : undefined}
              onChange={(event) => setCustomText(event.target.value)}
              className="max-w-[12rem] font-mono"
            />
          </div>
        ) : null}

        <AppPreview accent={picked} />

        {picked && !accentPassesAA(picked) ? (
          <p className="text-[13px] text-ink-2" data-testid="app-colour-adjusted">
            The app makes this colour darker or lighter in each mode, so that everyone can read buttons and text. The
            preview shows the colours people see.
          </p>
        ) : null}

        <SaveBar>
          <Button loading={save.isPending} disabled={invalid || unchanged} onClick={submit}>
            Use this colour
          </Button>
        </SaveBar>
        {save.error ? <InlineError error={save.error} /> : null}
      </div>
    </FormSection>
  );
}

/** A small phone screen in light and in dark mode, drawn with the colours the app makes. */
function AppPreview({ accent }: { accent: string | null }) {
  const palette = accent ? paletteFromAccent(accent) : TEAMNA_ACCENT_ROLES;
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="app-colour-preview">
      {(["light", "dark"] as const).map((mode) => {
        const roles = palette[mode];
        const text = BRAND_TEXT[mode];
        return (
          <figure key={mode} className="m-0 flex flex-col gap-1.5">
            <figcaption className="eyebrow text-ink-2">{mode === "light" ? "Light mode" : "Dark mode"}</figcaption>
            <div
              data-mode={mode}
              className="flex flex-col gap-3 rounded-xl border px-4 py-4"
              style={{ backgroundColor: BRAND_SURFACES[mode].background, borderColor: text.border }}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[15px] font-semibold" style={{ color: text.primary }}>
                  Today
                </p>
                <p className="text-[13px] font-semibold" style={{ color: roles.accent }}>
                  See all
                </p>
              </div>
              <p
                className="w-fit rounded-pill px-3 py-1 text-[12px] font-semibold"
                style={{ backgroundColor: roles.accentSurface, color: roles.accentStrong }}
              >
                2 sessions left
              </p>
              <span
                data-testid="app-colour-button"
                className="flex h-10 items-center justify-center rounded-lg text-[14px] font-semibold"
                style={{ backgroundColor: roles.accent, color: roles.accentOnAccent }}
              >
                Book a session
              </span>
            </div>
          </figure>
        );
      })}
    </div>
  );
}
